import { Subject, firstValueFrom, takeUntil } from 'rxjs';import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';

import {
  CreateDefectCommand,
  DefectListItemDTO,
  GridResultDto,
} from 'src/app/DTOs/DefectDTO';
import { CustomerDTO } from 'src/app/DTOs/CustomerDTO';
import { MasterDataService } from 'src/app/Services/master-data.service';
import { DefectCardComponent } from '../defect-card/defect-card.component';
import { DefectAdminTreeNodeComponent } from './defect-admin-tree-node.component';
import { PrimeNgModule } from 'src/app/Shared/prime-ng/prime-ng.module';

export type DefectSeverityFilter = 'all' | 'critical' | 'major' | 'minor';

interface BreadcrumbSegment {
  id: number | null;
  title: string;
}

export interface DefectAdminTreeNode {
  defect: DefectListItemDTO;
  depth: number;
  isLeaf: boolean;
  children: DefectAdminTreeNode[];
  pathSegments: BreadcrumbSegment[];
  descendantCount: number;
  highestSeverity: Exclude<DefectSeverityFilter, 'all'> | null;
}

interface DefectSearchResult {
  defect: DefectListItemDTO;
  pathSegments: BreadcrumbSegment[];
  score: number;
}

@Component({
  selector: 'app-defects',
  standalone: true,
  imports: [PrimeNgModule, CommonModule,
    FormsModule,
    DefectCardComponent,
    DefectAdminTreeNodeComponent],
  templateUrl: './defects.component.html',
  styleUrl: './defects.component.css',
})
export class DefectsComponent implements OnInit, OnDestroy {
  loading = false;

  customers: CustomerDTO[] = [];
  selectedCustomer: CustomerDTO | null = null;

  searchText = '';
  severityFilter: DefectSeverityFilter = 'all';
  expandedIds = new Set<number>();
  focusedId: number | null = null;

  allDefects: DefectListItemDTO[] = [];
  tree: DefectAdminTreeNode[] = [];
  searchResults: DefectSearchResult[] = [];
  focusedBreadcrumb: BreadcrumbSegment[] = [];
  detailCards: DefectListItemDTO[] = [];
  detailPaneTitle = 'Root level';
  detailPaneSubtitle = 'Select a customer to load the defect catalog.';

  criticalLeafCount = 0;
  majorLeafCount = 0;
  minorLeafCount = 0;

  createDialog = false;
  deleteDialog = false;
  defectToDelete: DefectListItemDTO | null = null;
  newDefect: CreateDefectCommand = this.createEmptyDefect();

  private readonly destroy$ = new Subject<void>();
  private readonly defectsById = new Map<number, DefectListItemDTO>();
  private readonly childrenByParent = new Map<number | null, DefectListItemDTO[]>();
  private loadToken = 0;

  get selectedCustomerId(): number | null {
    return this.selectedCustomer?.customerId ?? null;
  }

  get hasCustomerSelected(): boolean {
    return this.selectedCustomerId != null;
  }

  get hasCatalog(): boolean {
    return this.allDefects.length > 0;
  }

  get catalogSummary(): string {
    if (!this.hasCustomerSelected) {
      return 'Select a customer to load the defect catalog';
    }

    return `${this.allDefects.length} nodes · ${this.criticalLeafCount} critical · ${this.majorLeafCount} major · ${this.minorLeafCount} minor`;
  }

  get hasActiveFilters(): boolean {
    return this.severityFilter !== 'all' || this.searchText.trim().length > 0;
  }

  get createContextLabel(): string {
    const parentId = this.createParentId();
    if (parentId == null) {
      return 'Root level';
    }

    const parent = this.defectsById.get(parentId);
    if (!parent) {
      return 'Root level';
    }

    return this.pathLabelOf(this.computePath(parent));
  }

  get focusedDefectIsDeletable(): boolean {
    if (this.focusedId == null) return false;
    const focused = this.defectsById.get(this.focusedId);
    if (!focused) return false;
    return !focused.hasChildren;
  }

  get focusedDefect(): DefectListItemDTO | null {
    if (this.focusedId == null) return null;
    return this.defectsById.get(this.focusedId) ?? null;
  }

  constructor(private readonly masterData: MasterDataService) { }

  ngOnInit(): void {
    this.loadCustomers();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onCustomerChange(): void {
    this.searchText = '';
    this.severityFilter = 'all';
    this.expandedIds = new Set<number>();
    this.focusedId = null;
    this.createDialog = false;

    if (!this.hasCustomerSelected) {
      this.resetCatalog();
      return;
    }

    this.loadCatalog();
  }

  onSearchInput(value: string): void {
    this.searchText = value ?? '';
    this.refreshDerivedState();
  }

  clearSearch(): void {
    this.searchText = '';
    this.refreshDerivedState();
  }

  setSeverityFilter(filter: DefectSeverityFilter): void {
    this.severityFilter = filter;
    this.refreshDerivedState();
  }

  onTreeNodeClick(node: DefectAdminTreeNode): void {
    if (!node.isLeaf) {
      this.toggleNode(node.defect.defectId);
    }
    this.focusNode(node.defect.defectId);
  }

  toggleNode(defectId: number): void {
    const next = new Set(this.expandedIds);
    if (next.has(defectId)) {
      next.delete(defectId);
    } else {
      next.add(defectId);
    }
    this.expandedIds = next;
  }

  setFocus(defectId: number | null): void {
    this.focusNode(defectId);
  }

  goToBreadcrumb(index: number): void {
    const segment = this.focusedBreadcrumb[index];
    if (!segment || segment.id == null) {
      this.focusNode(null);
      return;
    }

    this.focusNode(segment.id);
  }

  selectFromSearch(result: DefectSearchResult): void {
    this.clearSearch();
    this.focusNode(result.defect.defectId);
  }

  openChildren(item: DefectListItemDTO): void {
    this.focusNode(item.defectId);
  }

  toggleCreateDialog(): void {
    if (!this.hasCustomerSelected) {
      return;
    }

    this.createDialog = !this.createDialog;
    if (this.createDialog) {
      this.newDefect = this.createEmptyDefect();
    }
  }

  openDeleteDialog(defect: DefectListItemDTO): void {
    this.defectToDelete = defect;
    this.deleteDialog = true;
  }

  closeDeleteDialog(): void {
    this.deleteDialog = false;
    this.defectToDelete = null;
  }

  confirmDeleteDefect(): void {
    if (!this.defectToDelete) return;

    const defect = this.defectToDelete;
    const parentId = defect.parentId ?? null;

    this.masterData
      .deleteDefect(defect.defectId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.closeDeleteDialog();
          if (this.focusedId === defect.defectId) {
            const parent = parentId != null ? this.defectsById.get(parentId) : null;
            this.focusedId = parent?.defectId ?? null;
          }
          this.loadCatalog(this.focusedId);
        },
        error: (err) => {
          this.closeDeleteDialog();
          alert(err?.error ?? 'This defect cannot be deleted.');
        },
      });
  }

  createDefect(): void {
    const customerId = this.selectedCustomerId;
    if (customerId == null) {
      return;
    }

    const dto: CreateDefectCommand = {
      ...this.newDefect,
      customerId,
      parentId: this.createParentId(),
      titleDE: (this.newDefect.titleDE ?? '').trim(),
      titleEN: (this.newDefect.titleEN ?? '').trim(),
    };

    this.masterData
      .createDefect(dto)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (createdId) => {
          this.createDialog = false;
          this.loadCatalog(createdId);
        },
      });
  }

  reloadCatalog(): void {
    this.loadCatalog(this.focusedId);
  }

  trackByTreeNode = (_: number, node: DefectAdminTreeNode): number => node.defect.defectId;
  trackByCard = (_: number, defect: DefectListItemDTO): number => defect.defectId;
  trackByResult = (_: number, result: DefectSearchResult): number => result.defect.defectId;
  trackByBreadcrumb = (_: number, segment: BreadcrumbSegment): string =>
    `${segment.id ?? 'root'}-${segment.title}`;

  displayTitle(defect: Pick<DefectListItemDTO, 'defectId' | 'titleDE' | 'titleEN'>): string {
    return defect.titleDE?.trim() || defect.titleEN?.trim() || `#${defect.defectId}`;
  }

  secondaryTitle(defect: Pick<DefectListItemDTO, 'titleDE' | 'titleEN'>): string {
    const titleDE = defect.titleDE?.trim();
    const titleEN = defect.titleEN?.trim();
    if (!titleEN || titleEN === titleDE) {
      return '';
    }
    return titleEN;
  }

  pathLabelOf(path: BreadcrumbSegment[]): string {
    return path.map((segment) => segment.title).join(' › ');
  }

  private loadCustomers(): void {
    this.masterData
      .getCustomers(null, true, null)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.customers = res ?? [];
        },
        error: () => {
          this.customers = [];
        },
      });
  }

  private resetCatalog(): void {
    this.allDefects = [];
    this.defectsById.clear();
    this.childrenByParent.clear();
    this.tree = [];
    this.searchResults = [];
    this.focusedBreadcrumb = [];
    this.detailCards = [];
    this.detailPaneTitle = 'Root level';
    this.detailPaneSubtitle = 'Select a customer to load the defect catalog.';
    this.criticalLeafCount = 0;
    this.majorLeafCount = 0;
    this.minorLeafCount = 0;
  }

  private async loadCatalog(preferredFocusId: number | null = this.focusedId): Promise<void> {
    const customerId = this.selectedCustomerId;
    if (customerId == null) {
      this.resetCatalog();
      return;
    }

    const token = ++this.loadToken;
    this.loading = true;

    try {
      const defects = await this.fetchAllDefects(customerId);
      if (token !== this.loadToken) {
        return;
      }

      this.allDefects = defects;
      this.rebuildCollections();

      if (preferredFocusId != null && this.defectsById.has(preferredFocusId)) {
        this.focusedId = preferredFocusId;
        this.expandPath(preferredFocusId);
      } else if (this.focusedId != null && this.defectsById.has(this.focusedId)) {
        this.expandPath(this.focusedId);
      } else {
        this.focusedId = null;
      }

      this.refreshDerivedState();
    } finally {
      if (token === this.loadToken) {
        this.loading = false;
      }
    }
  }

  private async fetchAllDefects(customerId: number): Promise<DefectListItemDTO[]> {
    const all: DefectListItemDTO[] = [];
    const pendingParents: Array<number | null> = [null];

    while (pendingParents.length > 0) {
      const parentId = pendingParents.shift() ?? null;
      const currentLevel = await this.fetchLevel(customerId, parentId);
      all.push(...currentLevel);

      for (const defect of currentLevel) {
        if (defect.hasChildren) {
          pendingParents.push(defect.defectId);
        }
      }
    }

    return all;
  }

  private async fetchLevel(
    customerId: number,
    parentId: number | null,
  ): Promise<DefectListItemDTO[]> {
    const take = 200;
    let skip = 0;
    let total = 0;
    const items: DefectListItemDTO[] = [];

    do {
      const result = await firstValueFrom(
        this.masterData.getDefects(customerId, parentId, null, skip, take),
      );

      const page = (result as GridResultDto<DefectListItemDTO>)?.data ?? [];
      total = (result as GridResultDto<DefectListItemDTO>)?.total ?? page.length;
      items.push(...page);
      skip += page.length;

      if (page.length === 0) {
        break;
      }
    } while (skip < total);

    return items;
  }

  private rebuildCollections(): void {
    this.defectsById.clear();
    this.childrenByParent.clear();

    for (const defect of this.allDefects) {
      this.defectsById.set(defect.defectId, defect);

      const key = defect.parentId ?? null;
      const list = this.childrenByParent.get(key) ?? [];
      list.push(defect);
      this.childrenByParent.set(key, list);
    }

    for (const list of this.childrenByParent.values()) {
      list.sort(
        (a, b) =>
          a.number - b.number || this.displayTitle(a).localeCompare(this.displayTitle(b)),
      );
    }
  }

  private refreshDerivedState(): void {
    this.tree = this.buildLevel(null, 0, []);
    this.searchResults = this.buildSearchResults();
    this.focusedBreadcrumb = this.buildFocusedBreadcrumb();
    this.buildDetailPane();
    this.computeSeverityTotals();
  }

  private buildLevel(
    parentId: number | null,
    depth: number,
    parents: BreadcrumbSegment[],
  ): DefectAdminTreeNode[] {
    const list = this.childrenByParent.get(parentId) ?? [];
    const result: DefectAdminTreeNode[] = [];

    for (const defect of list) {
      const pathSegments = [...parents, { id: defect.defectId, title: this.displayTitle(defect) }];
      const children = this.buildLevel(defect.defectId, depth + 1, pathSegments);
      const isLeaf = children.length === 0;
      const passesSeverity = isLeaf
        ? this.matchesSeverityFilter(defect)
        : children.some((child) => this.subtreePassesSeverity(child));

      if (!passesSeverity) {
        continue;
      }

      result.push({
        defect,
        depth,
        isLeaf,
        children,
        pathSegments,
        descendantCount: children.reduce((sum, child) => sum + child.descendantCount, defect.childCount),
        highestSeverity: this.computeHighestSeverity(defect, children),
      });
    }

    return result;
  }

  private buildSearchResults(): DefectSearchResult[] {
    const raw = this.searchText.trim().toLowerCase();
    if (!raw) {
      return [];
    }

    const tokens = raw.split(/\s+/).filter(Boolean);
    if (tokens.length === 0) {
      return [];
    }

    const results: DefectSearchResult[] = [];
    for (const defect of this.allDefects) {
      if (!this.matchesSeverityFilter(defect)) {
        continue;
      }

      const score = this.scoreMatch(defect, tokens);
      if (score <= 0) {
        continue;
      }

      results.push({
        defect,
        pathSegments: this.computePath(defect),
        score,
      });
    }

    results.sort(
      (a, b) =>
        b.score - a.score ||
        a.defect.number - b.defect.number ||
        this.displayTitle(a.defect).localeCompare(this.displayTitle(b.defect)),
    );

    return results.slice(0, 60);
  }

  private buildFocusedBreadcrumb(): BreadcrumbSegment[] {
    if (!this.hasCustomerSelected) {
      return [];
    }

    const root: BreadcrumbSegment = { id: null, title: 'Defect catalog' };
    if (this.focusedId == null) {
      return [root];
    }

    const focused = this.defectsById.get(this.focusedId);
    if (!focused) {
      return [root];
    }

    return [root, ...this.computePath(focused)];
  }

  private buildDetailPane(): void {
    if (!this.hasCustomerSelected) {
      this.detailCards = [];
      this.detailPaneTitle = 'Root level';
      this.detailPaneSubtitle = 'Select a customer to load the defect catalog.';
      return;
    }

    if (this.focusedId == null) {
      this.detailCards = (this.childrenByParent.get(null) ?? []).filter((defect) =>
        this.matchesSeverityFilterOrChildren(defect),
      );
      this.detailPaneTitle = 'Root level';
      this.detailPaneSubtitle = 'Top-level defects and categories for the selected customer.';
      return;
    }

    const focused = this.defectsById.get(this.focusedId);
    if (!focused) {
      this.detailCards = [];
      this.detailPaneTitle = 'Root level';
      this.detailPaneSubtitle = 'Top-level defects and categories for the selected customer.';
      return;
    }

    // Root-Parent (parentId = null) → sich selbst + alle Nachkommen flach
    if (focused.parentId == null) {
      const descendants = this.getAllDescendants(focused.defectId).filter((defect) =>
        this.matchesSeverityFilterOrChildren(defect),
      );
      const selfPasses = this.matchesSeverityFilterOrChildren(focused);
      this.detailCards = [
        ...(selfPasses ? [focused] : []),
        ...descendants];
      this.detailPaneTitle = this.displayTitle(focused);
      this.detailPaneSubtitle = this.pathLabelOf(this.computePath(focused));
      return;
    }

    // Alle anderen → nur sich selbst
    this.detailCards = [focused].filter((defect) =>
      this.matchesSeverityFilterOrChildren(defect),
    );
    this.detailPaneTitle = this.displayTitle(focused);
    this.detailPaneSubtitle = this.pathLabelOf(this.computePath(focused));
  }

  private getAllDescendants(defectId: number): DefectListItemDTO[] {
    const result: DefectListItemDTO[] = [];
    const children = this.childrenByParent.get(defectId) ?? [];

    for (const child of children) {
      result.push(child);
      if (child.hasChildren) {
        result.push(...this.getAllDescendants(child.defectId));
      }
    }

    return result;
  }

  private computeSeverityTotals(): void {
    this.criticalLeafCount = this.allDefects.filter((d) => !d.hasChildren && d.critical).length;
    this.majorLeafCount = this.allDefects.filter((d) => !d.hasChildren && d.major).length;
    this.minorLeafCount = this.allDefects.filter((d) => !d.hasChildren && d.minor).length;
  }

  private focusNode(defectId: number | null): void {
    this.focusedId = defectId;
    this.refreshDerivedState();
  }

  private expandPath(defectId: number): void {
    const next = new Set(this.expandedIds);
    let cursor = this.defectsById.get(defectId);

    while (cursor) {
      next.add(cursor.defectId);
      cursor = cursor.parentId != null ? this.defectsById.get(cursor.parentId) : undefined;
    }

    this.expandedIds = next;
  }

  private createParentId(): number | null {
    if (this.focusedId == null) {
      return null;
    }

    const focused = this.defectsById.get(this.focusedId);
    if (!focused) {
      return null;
    }

    return focused.defectId;
  }

  private createEmptyDefect(): CreateDefectCommand {
    return {
      customerId: this.selectedCustomerId ?? 0,
      parentId: this.createParentId(),
      titleDE: '',
      titleEN: '',
      number: 0,
      critical: false,
      major: false,
      minor: false,
    };
  }

  private computePath(defect: DefectListItemDTO): BreadcrumbSegment[] {
    const path: BreadcrumbSegment[] = [];
    let cursor: DefectListItemDTO | undefined = defect;

    while (cursor) {
      path.unshift({ id: cursor.defectId, title: this.displayTitle(cursor) });
      cursor = cursor.parentId != null ? this.defectsById.get(cursor.parentId) : undefined;
    }

    return path;
  }

  private matchesSeverityFilter(defect: DefectListItemDTO): boolean {
    if (this.severityFilter === 'all') return true;
    if (this.severityFilter === 'critical') return defect.critical;
    if (this.severityFilter === 'major') return defect.major;
    return defect.minor;
  }

  private matchesSeverityFilterOrChildren(defect: DefectListItemDTO): boolean {
    if (this.severityFilter === 'all') return true;
    if (this.matchesSeverityFilter(defect)) return true;
    const children = this.childrenByParent.get(defect.defectId) ?? [];
    return children.some((child) => this.matchesSeverityFilterOrChildren(child));
  }

  private subtreePassesSeverity(node: DefectAdminTreeNode): boolean {
    if (this.severityFilter === 'all') return true;
    if (node.isLeaf) return this.matchesSeverityFilter(node.defect);
    return node.children.some((child) => this.subtreePassesSeverity(child));
  }

  private computeHighestSeverity(
    defect: DefectListItemDTO,
    children: DefectAdminTreeNode[],
  ): Exclude<DefectSeverityFilter, 'all'> | null {
    let best: Exclude<DefectSeverityFilter, 'all'> | null = defect.critical
      ? 'critical'
      : defect.major
        ? 'major'
        : defect.minor
          ? 'minor'
          : null;

    const rank = (s: Exclude<DefectSeverityFilter, 'all'> | null): number =>
      s === 'critical' ? 3 : s === 'major' ? 2 : s === 'minor' ? 1 : 0;

    for (const child of children) {
      if (rank(child.highestSeverity) > rank(best)) {
        best = child.highestSeverity;
      }
    }

    return best;
  }

  private scoreMatch(defect: DefectListItemDTO, tokens: string[]): number {
    const haystacks = [
      defect.titleDE?.toLowerCase() ?? '',
      defect.titleEN?.toLowerCase() ?? '',
      defect.number?.toString() ?? ''];

    let total = 0;
    for (const token of tokens) {
      let bestForToken = 0;

      for (const haystack of haystacks) {
        if (!haystack) continue;
        if (haystack === token) bestForToken = Math.max(bestForToken, 100);
        else if (haystack.startsWith(token)) bestForToken = Math.max(bestForToken, 60);
        else if (haystack.includes(token)) bestForToken = Math.max(bestForToken, 30);
      }

      if (bestForToken === 0) return 0;
      total += bestForToken;
    }

    return total;
  }
}
